import React, {useState} from "react";
import {Link, useParams} from "react-router-dom";
import {useGetFilterDetails} from "helpers/useGetFilterDetails";
import DownloadButton from "components/ui/DownloadButton";
import QuickFilterBar from "components/QuickFilterBar";
import Tabs, { TabInfo } from "components/ui/Tabs";
import { useHideResize } from "helpers/useHideResize";
import { Spinner } from "components/ui/Spinner";
import { endpoints } from "config";
import api from "api";
import { useAsyncFunction } from "helpers/useAsyncFunction";
import { useSelector } from "react-redux";
import { selectDatabaseName } from "reducers/client";
import { selectPortalConfig } from "reducers/configuration";
import { v4 as uuidv4 } from "uuid";
//import FilterMenu from "components/FilterMenu/FilterMenu";
import MobileFilterForm from "components/MobileFilterMenu/MobileFilterForm";
import Private from "components/Private";
import Frame from "components/Frame";
import "styles/DataTable.scss";
import { Cell } from "helpers/tables/Cell";
import Sort from "assets/Icon_SortBy_01.png";
import SortASC from "assets/Icon_SortBy_03.png";
import SortDESC from "assets/Icon_SortBy_02.png";

const downloadUrl = endpoints.leagueTable.downloadLeagueTablesCSV;

type downloadFormat = "CSV" | "ZIP" | "XLSX";
type downloadData = "RAW" | "FILTERED";

type Sorting = { [k: string]: Record<string, "ASC" | "DESC" | "RESET"> };

export type ListColumn = {
  columnKey: string;
  labelValue: string;
  bVisible: boolean;
  bLocked: boolean;
};

type Values = {
  columns: ListColumn[];
  hierarchies: { label: string; value: string }[];
};

const initialValues: Values = {
  columns: [],
  hierarchies: [],
};

function getInitialValues(value?: string): Values {
  try {
    if (value) {
        let parsedJson = JSON.parse(value);
        if (parsedJson.hierarchies && !Array.isArray(parsedJson.hierarchies)) {
            parsedJson.hierarchies = [parsedJson.hierarchies];
        }
        return parsedJson;
    }
    return initialValues;
  } catch (_err) {
    return initialValues;
  }
}

const LeagueTables: React.FC = () => {
  const [bind, resizing] = useHideResize();
  const databaseName = useSelector(selectDatabaseName);
  const {projectId} = useParams();

  const handleOption = () => {
    return null;
  };

  const portalConfig = useSelector(selectPortalConfig);
  const [filters, setFilters] = React.useState("");
  const filterDetails = useGetFilterDetails(parseInt(projectId || "0", 10));
  const leagueTablesConfig = getInitialValues(portalConfig?.LeagueTablesConfiguration);
  const [activeTab, setActiveTab] = useState(
    leagueTablesConfig.hierarchies.reverse()[0]?.value ?? ""
  );
  const [sorting, setSorting] = React.useState<Sorting>(() => {
    const val: Sorting = {};
    for (const hierarchy of leagueTablesConfig.hierarchies) {
      val[hierarchy.value] = {};
    }
    return val;
  });

  const customName = useSelector(selectPortalConfig)?.LeagueTablesCustomName ?? "League tables";

  const handleTab = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActiveTab(event.currentTarget.id);
  };

  const tabs: TabInfo[] = leagueTablesConfig.hierarchies.map((hierarchy) => ({
    tabName: hierarchy.value,
    label: hierarchy.label,
    onclick: handleTab,
  }));

  const changeSorting = (hierarchy: string, column: string) => {
    const currentSort = sorting[hierarchy] ? sorting[hierarchy][column] : undefined;
    const nextSort = currentSort === "DESC" ? "ASC" : currentSort === "ASC" ? "RESET" : "DESC";
    if (nextSort === "RESET") {
      const newSorting = { ...sorting[hierarchy] };
      delete newSorting[column];
      setSorting((p) => ({ ...p, [hierarchy]: newSorting }));
      // delete newSort[key];
    } else {
      setSorting((p) => ({
        ...p,
        [hierarchy]: { ...p[hierarchy], [column]: nextSort },
      }));
    }
  };

  const getTabData = React.useCallback(
    async function() {
      if (portalConfig) {
        const leagueTablesConfig = getInitialValues(portalConfig?.LeagueTablesConfiguration);
        const bClearFilters = filters === "reset";
        const filterParam = filters === "reset" ? "" : filters;
        const uuid = uuidv4();
        const { data } = await api.post(endpoints.leagueTable.getLeagueTables, {
          projectId: projectId ? parseInt(projectId, 10) : 0,
          databaseName,
          filters: filterParam || undefined,
          bClearFilters,
          uuid,
          sortBy: JSON.stringify(sorting),
          columns: JSON.stringify(
            leagueTablesConfig.columns
              .filter((x) => x.bVisible)
              .map((x, i) => ({
                columnKey: x.columnKey,
                labelValue: x.labelValue,
                orderNumber: i + 1,
              }))
          ),
          leagueTableNames: leagueTablesConfig.hierarchies.map((x) => x.value),
        });

        if (data.status !== "1") throw new Error("Could not load league table data");

        const { details, metadata } = data;

        return { details, metadata };
      }
    },
    [projectId, portalConfig, databaseName, filters, sorting]
  );

  async function downloadFile(
    url: string,
    downloadData: downloadData,
    downloadFormat: downloadFormat
  ) {
    const leagueTablesConfig = getInitialValues(portalConfig?.LeagueTablesConfiguration);
    const filtered = downloadData === "FILTERED" ? true : false;
    const format = downloadFormat === "CSV" ? "text/plain" : "octet/stream";
    const filterParam = filters === "reset" ? "" : filters;
    const xlsx = downloadFormat == "XLSX";

    const leagueTableFileNames: Record<string, string> = {};
    leagueTablesConfig.hierarchies.forEach((h) => {
      leagueTableFileNames[h.value] = h.label;
    });

    try {
      const { data, headers } = await api.post(
        url,
        {
          projectId: parseInt(projectId ? projectId : ""),
          databaseName,
          leagueTableNames: leagueTablesConfig.hierarchies.map((x) => x.value),
          filters: filterParam || "",
          columns: JSON.stringify(
            leagueTablesConfig.columns
              .filter((x) => x.bVisible)
              .map((x, i) => ({
                columnKey: x.columnKey,
                labelValue: x.labelValue,
                orderNumber: i + 1,
              }))
          ),
          bDownloadFilteredData: filtered,
          bXlsx: xlsx,
          leagueTableFileNames,
          selectedTab: activeTab,
        },
        { responseType: downloadFormat === "ZIP" ? "arraybuffer" : "blob" }
      );
      const link = document.createElement("a");
      link.download = "data.json";
      const contentDisposition = headers["content-disposition"];
      const filename = contentDisposition.match(/filename=(.*); /)[1];

      const blob = new File([data], filename, { type: format });

      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    } catch (err) {
      throw err;
    }
  }

  const tableData = useAsyncFunction(getTabData);

  const currentTable = tableData.data ? tableData.data.details[activeTab] : null;

  const rightMenu = tableData.data?.metadata ? (
    <MobileFilterForm
      filterDetails={filterDetails}
      projectId={projectId}
      filters={tableData.data?.metadata?.filterString ?? ""}
      setFilters={setFilters}
      commentAnalysis={true}
      onlyNouns
    />
  ) : null;

  const content = () => {
    if (!portalConfig) return <Spinner />;

    if (tableData.error) throw tableData.error;

    const locationSortByValue = sorting[activeTab] ? sorting[activeTab]["Name"] : "DESC";
    //const locationSortByValue = sorting[activeTab]["Name"];
    let locSortSrc = Sort;
    if (locationSortByValue === "ASC") locSortSrc = SortASC;
    if (locationSortByValue === "DESC") locSortSrc = SortDESC;

    const columnHeadings = tableData.data
      ? [
          <Cell key="colname">
            <button
              type="button"
              onClick={() => changeSorting(activeTab, "Name")}
              className="Sort"
              title={""}
            >
              <span key={"th-name"}>
                {leagueTablesConfig.hierarchies
                  .find((x) => x.value === activeTab)
                  ?.label.toUpperCase()}
              </span>
              <img src={locSortSrc} alt="Sort" />
            </button>
          </Cell>,
        ].concat(
          currentTable?.columns.map((x: any, i: number) => {
            const sortByValue = sorting[activeTab][x.columnKey];
            let src = Sort;
            if (sortByValue === "ASC") src = SortASC;
            if (sortByValue === "DESC") src = SortDESC;
            return (
              <Cell key={i}>
                {true ? (
                  <button
                    type="button"
                    onClick={() => changeSorting(activeTab, x.columnKey)}
                    className="Sort"
                    title={x.labelValue}
                  >
                    <span>{x.labelValue}</span>
                    <img src={src} alt="Sort" />
                  </button>
                ) : (
                  <div className="Sort" title={x.labelValue}>
                    <span>{x.labelValue.toUpperCase()}</span>
                  </div>
                )}
              </Cell>
            );
          })
        )
      : [];

    const rows = tableData.data
      ? currentTable?.listData.map((row: any, i: number) => {
          const dataCells: any[] = [];
          for (const col of currentTable?.columns.sort((a: any, b: any) =>
            a.orderNumber > b.orderNumber ? 1 : -1
          ) ?? []) {
            dataCells.push(row[col.columnKey] ?? null);
          }

          return (
            <div className="Row" key={i}>
              {[
                <Cell key="row-name">
                  <span>
                      {row["Name"]}
                  </span>
                </Cell>,
              ].concat(
                dataCells.map((cell: any, i) => {
                  return (
                    <Cell key={i}>
                      <div className="CardTableItem">
                        {typeof cell === "string" ? (
                          <div>{cell}</div>
                        ) : cell != null ? (
                          <div>
                            <span className="Score">
                              {cell && cell.score != null ? `${cell.score.toFixed(1)}%` : "---"}{" "}
                            </span>
                            <span className="Respondents">
                              {cell && cell.count ? `(${cell.count} resp)` : ""}
                            </span>
                          </div>
                        ) : (
                          <div>N/A</div>
                        )}
                      </div>
                    </Cell>
                  );
                })
              )}
            </div>
          );
        })
      : [];

    const showQuickFilter = !!tableData.data?.metadata?.filterString;

    const topclass = showQuickFilter ? "WidthContent" : "WidthContent HeightContent";

    return (
      <div className={topclass}>
        {tableData.data?.metadata?.filterString && (
          <QuickFilterBar
            filterString={tableData.data?.metadata?.filterString ?? ""}
            setFilters={setFilters}
            filterDetails={filterDetails}
          />
        )}
        <div id="LeagueTable" {...bind}>
          <div id="TabFlex1" className="TabFlex">
            <Tabs tabs={tabs} activeTab={activeTab} />
          </div>
          <div className="HeadingFlex">
            <div>
              <h1>{customName}</h1>
            </div>
            <div id="TabFlex2" className="TabFlex">
              <Tabs tabs={tabs} activeTab={activeTab} />
            </div>
            <div>
              <DownloadButton
                options={true}
                label="Download"
                onClick={() => downloadFile(downloadUrl, "RAW", "ZIP")}
                onClickFiltered={() => downloadFile(downloadUrl, "FILTERED", "ZIP")}
                onClickXlsx={() => downloadFile(downloadUrl, "FILTERED", "XLSX")}
              />
            </div>
          </div>
          {resizing || !tableData.data ? (
            <Spinner />
          ) : (
            <div className="TableWrapper">
              <div className="TableContainer">
                <div className="MainTable NoOptions">
                  <div className="MainTableHeader">
                    {columnHeadings}
                    {/* <Cell /> */}
                  </div>
                  <div className="MainTableBody">{rows}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Frame
      rightMenu={rightMenu}
      userAccessField="bCanAccessLeagueTables"
      portalConfigField="bLeagueTablesVisible"
    >
      {content()}
    </Frame>
  );
};

export function LeagueTable(props: { firstColName: string; table: any; projectId: string }) {
  const { table, projectId, firstColName } = props;
  const columnHeadings = [
    <Cell key={"th-name"}>
      <span>{firstColName.toUpperCase()}</span>
    </Cell>,
  ].concat(
    table.columns.map((x: any, i: number) => (
      <Cell key={i}>
        <span>{x.labelValue.toUpperCase()}</span>
      </Cell>
    ))
  );

  const rows = table.listData.map((row: any, i: number) => {
    const dataCells: any[] = [];
    for (const col of table.columns.sort((a: any, b: any) =>
      a.orderNumber > b.orderNumber ? 1 : -1
    ) ?? []) {
      dataCells.push(row[col.columnKey] ?? null);
    }

    return (
      <div className="Row" key={i}>
        {[
          <Cell key="row-name">
            <span>
              {/* <Link
                className="ReviewFeedLink"
                to={`/project/${projectId}/dashboard?fs=${row["ReviewFeedFilterString"]}`}
              >
                {row["Name"]}
              </Link> */}
              {row["Name"]}
            </span>
          </Cell>,
        ].concat(
          dataCells.map((cell: any, i) => {
            return (
              <Cell key={i}>
                <div className="CardTableItem">
                  {typeof cell === "string" ? (
                    <div>{cell}</div>
                  ) : (
                    <div>
                      <span className="Score">
                        {cell && cell.score ? `${cell.score.toFixed(1)}%` : "---"}{" "}
                      </span>
                      <span className="Respondents">
                        {cell && cell.count ? `(${cell.count} resp)` : ""}
                      </span>
                    </div>
                  )}
                </div>
              </Cell>
            );
          })
        )}
      </div>
    );
  });

  return (
    <div className="TableWrapper">
      <div className="TableContainer">
        <div className="MainTable NoOptions">
          <div className="MainTableHeader">{columnHeadings}</div>
          <div className="MainTableBody">{rows}</div>
        </div>
      </div>
    </div>
  );
}

export default (
  props: JSX.IntrinsicAttributes
) => (
  <Private portalConfigField="bLeagueTablesVisible">
    <LeagueTables {...props} />
  </Private>
);
